import React, { useState, useContext } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { gql, useQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import {
  LoadingView,
  NotificationContext,
} from "@solsys/solsys-reactjs";
import { TbTicket, TbSettings, TbMapPin, TbBell, TbChartBar, TbFileReport } from "react-icons/tb";
import MultiView from "../mapview/MultiView";

const DashboardContainer = styled.div`
  padding: 20px;
  height: calc(100vh - 40px);
  overflow-y: auto;
  background-color: var(--app-bg);
  color: var(--text-color);
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background: var(--card-bg);
  color: var(--text-color);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 20px;
  box-shadow: var(--shadow-card);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  & svg {
    color: inherit;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-card-hover);
  }
`;

const StatCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  color: var(--text-muted);
  font-size: 14px;
  font-weight: 500;
`;

const StatCardValue = styled.div`
  font-size: 32px;
  font-weight: bold;
  color: var(--text-color);
`;

const StatCardSubtext = styled.div`
  font-size: 12px;
  color: var(--text-subtle);
  margin-top: 5px;
`;

const MapSection = styled.div`
  background: var(--card-bg);
  color: var(--text-color);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 20px;
  box-shadow: var(--shadow-card);
  margin-bottom: 20px;
  height: 500px;
`;

const MapHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  color: var(--text-color);
  font-size: 18px;
  font-weight: 600;

  & svg {
    color: var(--text-color);
  }
`;

const NotificationSection = styled.div`
  background: var(--card-bg);
  color: var(--text-color);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 20px;
  box-shadow: var(--shadow-card);
  max-height: 400px;
  overflow-y: auto;
`;

const NotificationItem = styled.div`
  padding: 12px;
  border-bottom: 1px solid var(--table-row-border);
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--surface-muted);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NotificationTitle = styled.div`
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 4px;
`;

const NotificationBody = styled.div`
  font-size: 14px;
  color: var(--text-muted);
  margin-bottom: 4px;
`;

const NotificationTime = styled.div`
  font-size: 12px;
  color: var(--text-subtle);
`;

const ticketStatsQuery = gql`
  query ticketStats(
    $all: ticket_list_request
    $open: ticket_list_request
    $closed: ticket_list_request
  ) {
    all: ticket(ticket_list_request: $all) {
      totalCount
      edges {
        id
      }
    }
    open: ticket(ticket_list_request: $open) {
      totalCount
      edges {
        id
      }
    }
    closed: ticket(ticket_list_request: $closed) {
      totalCount
      edges {
        id
      }
    }
  }
`;

const serviceCountQuery = gql`
  query serviceCounts($service_list_request: service_list_request) {
    service(service_list_request: $service_list_request) {
      totalCount
      edges {
        id
      }
    }
  }
`;

const craneCountQuery = gql`
  query craneCounts($crane_list_request: crane_list_request) {
    crane(crane_list_request: $crane_list_request) {
      totalCount
      edges {
        id
      }
    }
  }
`;

const countListRequest = (filters = []) => ({
  pagination: { limit: 1 },
  filters,
  sorting: [],
});

export default function Dashboard() {
  const history = useHistory();
  const { user } = useAuth();
  const { addNotification } = useContext(NotificationContext);
  const [notifications, setNotifications] = useState([]);

  const { loading: ticketLoading, data: ticketData } = useQuery(ticketStatsQuery, {
    variables: {
      all: countListRequest(),
      open: countListRequest([{ field: "status_id", operator: "=", value: "1" }]),
      closed: countListRequest([{ field: "status_id", operator: "=", value: "2" }]),
    },
    fetchPolicy: "network-only",
    onError: (error) => {
      console.error("Error fetching ticket counts:", error);
    },
  });

  const { loading: serviceLoading, data: serviceData, error: serviceError } = useQuery(serviceCountQuery, {
    variables: {
      service_list_request: countListRequest(),
    },
    fetchPolicy: "network-only",
    onError: (error) => {
      console.error("Error fetching service counts:", error);
      addNotification("bad", "Failed to load service count");
    },
  });

  // Fetch crane counts
  const { loading: craneLoading, data: craneData, error: craneError } = useQuery(craneCountQuery, {
    variables: {
      crane_list_request: countListRequest(),
    },
    fetchPolicy: "network-only",
    onError: (error) => {
      console.error("Error fetching crane counts:", error);
      addNotification("bad", "Failed to load crane count");
    },
  });

  const totalTickets = ticketData?.all?.totalCount ?? 0;
  const openTickets = ticketData?.open?.totalCount ?? 0;
  const closedTickets = ticketData?.closed?.totalCount ?? 0;
  const totalServices = serviceData?.service?.totalCount ?? 0;
  const totalCranes = craneData?.crane?.totalCount ?? 0;

  const loading = ticketLoading || serviceLoading || craneLoading;

  if (loading) {
    return <LoadingView />;
  }

  return (
    <DashboardContainer>
      <DashboardGrid>
        <StatCard onClick={ () => history.push("/ticket/list") }>
          <StatCardHeader>
            <TbTicket size={ 20 } />
            <span>Total Tickets</span>
          </StatCardHeader>
          <StatCardValue>{ totalTickets }</StatCardValue>
          <StatCardSubtext>
            { openTickets } open, { closedTickets } closed
          </StatCardSubtext>
        </StatCard>

        <StatCard onClick={ () => history.push("/ticket/list?statusId=1") }>
          <StatCardHeader>
            <TbTicket size={ 20 } style={ { color: "#e74c3c" } } />
            <span>Open Tickets</span>
          </StatCardHeader>
          <StatCardValue style={ { color: "#e74c3c" } }>{ openTickets }</StatCardValue>
          <StatCardSubtext>Requires attention</StatCardSubtext>
        </StatCard>

        <StatCard onClick={ () => history.push("/services/list") }>
          <StatCardHeader>
            <TbSettings size={ 20 } />
            <span>Total Services</span>
          </StatCardHeader>
          <StatCardValue>{ totalServices }</StatCardValue>
          <StatCardSubtext>Service records</StatCardSubtext>
        </StatCard>

        <StatCard onClick={ () => history.push("/ecrane/list") }>
          <StatCardHeader>
            <TbMapPin size={ 20 } />
            <span>Total Cranes</span>
          </StatCardHeader>
          <StatCardValue>{ totalCranes }</StatCardValue>
          <StatCardSubtext>Active cranes</StatCardSubtext>
        </StatCard>

        <StatCard onClick={ () => history.push("/dashboard/management") }>
          <StatCardHeader>
            <TbChartBar size={ 20 } style={ { color: "#0d6efd" } } />
            <span>Management &amp; Insights</span>
          </StatCardHeader>
          <StatCardValue style={ { fontSize: 20 } }>View</StatCardValue>
          <StatCardSubtext>Inspection reports &amp; graphs</StatCardSubtext>
        </StatCard>

        <StatCard onClick={ () => history.push("/fsr/add") }>
          <StatCardHeader>
            <TbFileReport size={ 20 } style={ { color: "#198754" } } />
            <span>Quick FSR</span>
          </StatCardHeader>
          <StatCardValue style={ { fontSize: 20, color: "#198754" } }>+ New</StatCardValue>
          <StatCardSubtext>Create a field service report</StatCardSubtext>
        </StatCard>
      </DashboardGrid>

      <MapSection>
        <MapHeader>
          <TbMapPin size={ 24 } />
          <span>Crane Map</span>
        </MapHeader>
        <div style={ { height: "calc(100% - 50px)" } }>
          <MultiView style={ { height: "100%" } } />
        </div>
      </MapSection>

      { notifications.length > 0 && (
        <NotificationSection>
          <MapHeader>
            <TbBell size={ 24 } />
            <span>Recent Notifications</span>
          </MapHeader>
          { notifications.map((notification, index) => (
            <NotificationItem
              key={ index }
              onClick={ () => notification.path && history.push(notification.path) }
            >
              <NotificationTitle>{ notification.title }</NotificationTitle>
              <NotificationBody>{ notification.body }</NotificationBody>
              <NotificationTime>{ notification.time }</NotificationTime>
            </NotificationItem>
          )) }
        </NotificationSection>
      ) }
    </DashboardContainer>
  );
}

